import { ChevronRight } from "lucide-react";
import { useRef, useState, type FC } from "react";

import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/ui/status-badge";
import { cn } from "@/lib/utils";

import { chapterAt, formatTimestamp, isNewTutorial } from "../catalog";
import type { Tutorial, UpcomingTutorial } from "../types";
import { TutorialVideo } from "./TutorialVideo";

type Props = {
  tutorial: Tutorial;
  /** Seconds in to start at, for a chapter link. */
  initialTime?: number;
  /** The next tutorial in the course, for Up Next. */
  next?: Tutorial;
  /** Shown as Coming Soon once there's no next tutorial. */
  upcoming: UpcomingTutorial[];
  onSelect: (id: string) => void;
};

const minutes = (t: Tutorial) => `${Math.round(t.durationSecs / 60)} min`;

/** One tutorial as a lesson: the video, what's in it, and what's next. */
export const TutorialLesson: FC<Props> = ({
  tutorial,
  initialTime = 0,
  next,
  upcoming,
  onSelect
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [time, setTime] = useState(initialTime);
  const playing = chapterAt(tutorial.chapters, time);

  const jumpTo = (start: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = start;
    setTime(start);
    video.play().catch(() => undefined);
  };

  return (
    <article className="mx-auto box-content flex max-w-192 flex-col gap-8 px-10 pt-8 pb-24">
      <TutorialVideo
        ref={videoRef}
        tutorial={tutorial}
        startTime={initialTime}
        onTimeUpdate={setTime}
      />

      <div className="mx-auto flex w-full max-w-170 flex-col gap-5">
        <header className="flex flex-col gap-1">
          <span className="text-sm text-fg-3">{tutorial.topic}</span>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-medium text-fg">{tutorial.title}</h1>
            {isNewTutorial(tutorial) ? (
              <StatusBadge tone="accent">New</StatusBadge>
            ) : null}
          </div>
          <span className="text-sm text-fg-3">
            {minutes(tutorial)}
            {tutorial.chapters.length
              ? ` · ${tutorial.chapters.length} chapters`
              : ""}
          </span>
        </header>

        <p className="text-md text-fg-2">{tutorial.description}</p>

        {tutorial.chapters.length ? (
          <section className="flex flex-col gap-1.5">
            <h2 className="text-base font-medium text-fg">In This Video</h2>
            <nav aria-label="Chapters" className="flex flex-col">
              {tutorial.chapters.map((c, i) => (
                <button
                  key={c.start}
                  type="button"
                  aria-label={`${formatTimestamp(c.start)} ${c.title}`}
                  aria-current={i === playing ? "true" : undefined}
                  onClick={() => jumpTo(c.start)}
                  className={cn(
                    "flex min-w-0 cursor-pointer gap-3 py-0.5 text-left text-md outline-none hover:text-fg focus-visible:text-fg",
                    i === playing ? "text-fg" : "text-fg-2"
                  )}
                >
                  <span
                    className={cn(
                      "w-12 shrink-0 tabular-nums",
                      i === playing ? "text-accent-seed" : "text-fg-3"
                    )}
                  >
                    {formatTimestamp(c.start)}
                  </span>
                  <span className="truncate">{c.title}</span>
                </button>
              ))}
            </nav>
          </section>
        ) : null}

        {next ? (
          <section
            aria-label="Up Next"
            className="mt-3 flex flex-col gap-0.5 rounded-xl bg-fill-1 p-4 ring-1 ring-line-2"
          >
            <span className="text-sm text-fg-3">Up Next</span>
            <span className="text-base font-medium text-fg">{next.title}</span>
            <span className="text-sm text-fg-3">
              {next.topic} · {minutes(next)}
            </span>
            <Button
              variant="link"
              size="sm"
              className="mt-1 self-start px-0"
              onClick={() => onSelect(next.id)}
            >
              Watch Next
              <ChevronRight data-icon="inline-end" />
            </Button>
          </section>
        ) : upcoming.length ? (
          <section
            aria-label="Coming Soon"
            className="mt-3 overflow-hidden rounded-xl ring-1 ring-line-2"
          >
            <div className="flex flex-col gap-0.5 bg-fill-1 p-4">
              <span className="text-base font-medium text-fg">Coming Soon</span>
              <span className="text-sm text-fg-3">
                More tutorials are on the way. Each one pops up here when
                it&apos;s released.
              </span>
            </div>
            <ul className="divide-y divide-line-1 border-t border-line-1">
              {upcoming.map((u) => (
                <li
                  key={u.id}
                  className="flex items-center justify-between gap-3 px-4 py-2.5"
                >
                  <span className="truncate text-base text-fg-2">
                    {u.title}
                  </span>
                  <span className="shrink-0 text-sm text-fg-3">{u.topic}</span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>
    </article>
  );
};
